import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  GeneralSettingValue,
  GeneralSettingValueService,
  PageableResponse,
  SocietiesService,
  Society,
  StringUtils,
  UserService,
} from '@pwc-bei/common';
import { FilterValues } from '@pwc-bei/forms';
import { debounceTime, switchMap } from 'rxjs/operators';
import { BasePage } from '../../base-page/base-page';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import { AuthenticationCookie, AuthenticationService } from '@pwc-bei/security';
import { RoutePath } from 'src/app/enums/route-path.enum';
import { AuthenticationUtilsService } from 'src/app/services/authentication-utils.service';
import { SocietySelectionUtils } from 'src/app/utils/society-selection-utils';
import { environment } from 'src/environments/environment';
import { FaqThematicAreaEnum } from '../enum/faq-thematic-area.enum';
import { IncentiveFaqResource } from '../models/api/incentiveFaq-resource';
import { IncentiveFaq } from '../models/incentiveFaq';
import { IncentiveFaqFilter } from '../models/incentiveFaq-filter';
import { IncentiveFaqService } from '../service/incentiveFaq.service';
import { EAccessPlanVariable } from 'src/app/enum/access-plan-variable';
import { faChevronLeft, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: '' + 'app-faq-incentivi',
    templateUrl: './incentiveFaq-list.component.html',
    styleUrls: ['./incentiveFaq-list.component.scss'],
    standalone: false
})
export class IncentiveFaqListComponent
  extends BasePage
  implements OnInit, AfterViewInit {
  question: string;
  category: string;

  label;
  id;
  filters: IncentiveFaqFilter;

  private emptyFilters: IncentiveFaqFilter = {
    category: null,
    question: null,
  };
  faChevronLeft = faChevronLeft;
  faCircleNotch = faCircleNotch;

  categoryNumber: number;
  incentiveFaqs?: IncentiveFaq[];
  incentiveFaqsCategories: Array<string>;
  incentiveFaqsQuestion: Set<string>;
  answerAccordion = true;
  helpDeskEmail: string;
  isCategorySelected: boolean = false;

  subtitle = '';
  subtitle2 = '';
  defaultSubtitle = 'rda.subtitle-1-v1';
  subtitleChange = 'rda.subtitle-1-v2';
  subtitleChange2 = 'rda.subtitle-2-v2';

  initialized = false;
  emptyUIFilter: FilterValues = {
    category: null,
    answer: null,
    question: null,
  };

  UIFilter: FilterValues = { ...this.emptyUIFilter };
  logger: any;

  showFaq: boolean = false;
  isUserSmart: boolean = false;

  constructor(
    private router: Router,
    private incentiveFaqService: IncentiveFaqService,
    private changeDetector: ChangeDetectorRef,
    private societiesService: SocietiesService,
    private generalSettingValueService: GeneralSettingValueService,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private authenticationUtilsService: AuthenticationUtilsService,
    private route: ActivatedRoute,
    private translateService: TranslateService
  ) {
    super();
  }

  get faAngleDown() {
    return faAngleDown;
  }

  get faAngleUp() {
    return faAngleUp;
  }

  toggleAccordion(item: IncentiveFaq) {
    if (item.collapsed === null || item.collapsed === undefined) {
      item.collapsed = true;
      return;
    }

    item.collapsed = !item.collapsed;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.subtitle = this.translateService.instant(this.defaultSubtitle)

    this.isUserSmart = this.authenticationUtilsService.getAccessPlan(EAccessPlanVariable.IS_USER_SMART) ? true : false;
    this.isUserSmart ? this.router.navigate([RoutePath.INCENTIVI, RoutePath.TAX_BREAK]) : this.showFaq = true;

    this.societiesService.currentSociety$.subscribe((society) => {
      if (society == null) {
        this.userService
          .getSocietiesForGroupCodes(SocietySelectionUtils.GROUP_CODES)
          .subscribe((societies: Society[]) => {
            if (societies.length > 1) {
              this.router.navigate([
                RoutePath.INCENTIVI,
                RoutePath.TAX_BREAK,
                RoutePath.SOCIETY_SELECTION,
              ]);
            } else {
              if (societies.length > 0) {
                this.societiesService.setSociety(
                  societies[societies.length - 1],
                );
              }
            }
          });
      }
    });

    this.subscriptions.push(
      this.incentiveFaqService.pagination$.subscribe(
        (pagination: PageableResponse<IncentiveFaqResource[]>) => {
          this.pagination = { ...pagination };
        },
      ),
    );

    this.initHelpDeskEmail();
    this.initSearch();
    this.onSearch();

    this.filters = {
      ...this.emptyFilters,
    };
  }

  ngAfterViewInit(): void {
    this.initialized = true;
    this.changeDetector.detectChanges();
    window.scrollTo(0, 0);
  }

  private initSearch(): void {
    this.subscriptions.push(
      this.searchTrigger$
        .pipe(
          debounceTime(300),
          switchMap(() => this.societiesService.getSelectedSociety()),
          switchMap((society: Society) => {
            const search: Partial<IncentiveFaq> = this.getFilters(this.filters);

            search.page = this.currentPage;
            search.size = this.pageSize;
            search.filterSociety = society.id;
            search.filterFromModule = FaqThematicAreaEnum.BANDI_INCENTIVI;

            return this.incentiveFaqService.searchPost(search);
          }),
        )
        .subscribe((data: IncentiveFaq[]) => {
          this.incentiveFaqs = [...data];
          this.currentPage = 0;
          window.scrollTo(0, 0);
        }),
    );

    this.incentiveFaqService
      .getCategories()
      .subscribe((data: Array<string>) => {
        this.incentiveFaqsCategories = [...data];
      });
  }

  categoryIsSelected() {
    this.subtitle = this.translateService.instant(this.subtitleChange);
    this.subtitle2 = this.translateService.instant(this.subtitleChange2);
  }

  private initHelpDeskEmail(): void {
    this.societiesService
      .getSelectedSociety()
      .pipe(
        switchMap((society: Society) =>
          this.generalSettingValueService.findByCode(
            'HELPDESK_EMAIL',
            society.id,
          ),
        ),
      )
      .subscribe((list: GeneralSettingValue[]) => {
        if (list == null || list.length === 0) {
          this.helpDeskEmail = environment.helpDeskEmail;
          return;
        }

        const setting = list[0];
        this.helpDeskEmail = setting.value;
      });
  }

  goToHomepage() {
    this.router.navigate([
      RoutePath.HOME
    ]);
  }

  private getFilters($event) {
    if ($event === null || $event === undefined) {
      return {};
    }
    const filters = $event;
    const search: Partial<IncentiveFaq> = {};

    if (!StringUtils.isEmpty(filters.question)) {
      search.question = filters.question;
    }

    if (!StringUtils.isEmpty(filters.category)) {
      search.category = filters.category;
    }

    return search;
  }
}
